import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { logLogin } from '../lib/auditLog';

function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        navigate('/');
      }
    });
  }, [navigate]);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    setResetLoading(true);
    setError('');

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      setError('Unable to send reset email. Please try again.');
    } else {
      setResetSent(true);
    }
    setResetLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      // Sanitize error messages to prevent information leakage
      const safeMessage = error.message?.includes('Invalid login')
        ? 'Invalid email or password'
        : 'Unable to sign in. Please try again.';
      setError(safeMessage);
      setLoading(false);
    } else {
      // Log successful login
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        logLogin(user.id, user.email);
      }
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-black">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
        <div className="flex justify-center mb-4">
          <img src="/favicon.svg" alt="Swyft Restoration" className="h-16 object-contain rounded-xl" />
        </div>
        <h1 className="text-2xl font-bold text-center text-brand-black mb-6">
          Swyft Restoration
        </h1>

        {showForgotPassword ? (
          <div className="space-y-4">
            {resetSent ? (
              <div className="text-center">
                <div className="bg-green-50 text-green-700 p-4 rounded-md mb-4">
                  Password reset email sent! Check your inbox.
                </div>
                <button
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetSent(false);
                  }}
                  className="text-brand-coral hover:text-brand-coral-dark font-medium"
                >
                  Back to Sign In
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                {error && (
                  <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm">
                    {error}
                  </div>
                )}
                <p className="text-sm text-gray-600 text-center">
                  Enter your email and we'll send you a reset link.
                </p>
                <div>
                  <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    id="reset-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter your email"
                  />
                </div>
                <button
                  type="submit"
                  disabled={resetLoading}
                  className="w-full bg-brand-coral text-white py-2 px-4 rounded-md hover:bg-brand-coral-dark focus:outline-none focus:ring-2 focus:ring-brand-coral disabled:opacity-50"
                >
                  {resetLoading ? 'Sending...' : 'Send Reset Link'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setError('');
                  }}
                  className="w-full text-gray-600 hover:text-gray-800 text-sm"
                >
                  Back to Sign In
                </button>
              </form>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm">
                {error}
              </div>
            )}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your email"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-brand-coral text-white py-2 px-4 rounded-md hover:bg-brand-coral-dark focus:outline-none focus:ring-2 focus:ring-brand-coral disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowForgotPassword(true);
                setError('');
              }}
              className="w-full text-gray-500 hover:text-gray-700 text-sm"
            >
              Forgot password?
            </button>
            <p className="text-xs text-center text-gray-400 pt-2">
              By signing in you agree to use this system for authorized Swyft Restoration
              business only. Activity is logged.{' '}
              <Link to="/terms" className="underline hover:text-gray-600">
                Learn more
              </Link>
              {' · '}
              <Link to="/privacy" className="underline hover:text-gray-600">
                Privacy
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

export default Login;
